import { useEffect, useState } from "react";

interface Props {
  loading: boolean;
  label?: string; // e.g. "90 min" — shown while loading
}

/**
 * Elegant loading overlay for embedded maps.
 * - Blurs the map behind it
 * - Shows a dashed orbit ring with the ATR plane icon circling it
 * - Fades in/out smoothly so it never feels jarring
 */
export default function MapLoadingOverlay({ loading, label }: Props) {
  const [visible, setVisible] = useState(false);
  const [rendered, setRendered] = useState(false);

  useEffect(() => {
    if (loading) {
      setRendered(true);
      // Small delay so the blur feels intentional, not glitchy
      const t = setTimeout(() => setVisible(true), 60);
      return () => clearTimeout(t);
    } else {
      setVisible(false);
      const t = setTimeout(() => setRendered(false), 500);
      return () => clearTimeout(t);
    }
  }, [loading]);

  if (!rendered) return null;

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 800,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backdropFilter: visible ? "blur(6px)" : "blur(0px)",
        background: visible
          ? "rgba(4, 16, 20, 0.45)"
          : "rgba(4, 16, 20, 0)",
        transition: "backdrop-filter 0.4s ease, background 0.4s ease",
        opacity: visible ? 1 : 0,
        pointerEvents: "none",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 20,
          opacity: visible ? 1 : 0,
          transform: visible ? "scale(1)" : "scale(0.92)",
          transition: "opacity 0.4s ease 0.1s, transform 0.4s cubic-bezier(0.16,1,0.3,1) 0.1s",
        }}
      >
        {/* Orbit ring with ATR plane */}
        <div style={{ position: "relative", width: 96, height: 96 }}>
          {/* Dashed orbit circle */}
          <svg
            width="96"
            height="96"
            viewBox="0 0 96 96"
            style={{ position: "absolute", inset: 0 }}
          >
            <circle
              cx="48"
              cy="48"
              r="40"
              fill="none"
              stroke="rgba(0, 230, 195, 0.25)"
              strokeWidth="1.5"
              strokeDasharray="5 4"
            />
            {/* Inner glow ring */}
            <circle
              cx="48"
              cy="48"
              r="40"
              fill="none"
              stroke="rgba(0, 230, 195, 0.08)"
              strokeWidth="8"
            />
          </svg>

          {/* ATR plane orbiting — uses CSS animation */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              animation: "mapLoadOrbit 2.4s linear infinite reverse",
            }}
          >
            {/* Plane positioned at top of orbit (12 o'clock), pointing right (90°) */}
            <div
              style={{
                position: "absolute",
                top: 4,   // radius=40, center=48, so top = 48-40-4 = 4
                left: 48 - 10, // centered horizontally
                width: 20,
                height: 20,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img
                src="/photos/ATR-plane-icon.svg"
                alt=""
                style={{
                  width: 20,
                  height: 20,
                  objectFit: "contain",
                  filter: "brightness(0) invert(1) drop-shadow(0 0 6px rgba(0,230,195,0.8))",
                  transform: "rotate(90deg)", // nose points in direction of travel
                }}
              />
            </div>
          </div>

          {/* Center pulsing dot */}
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: "#009983",
              boxShadow: "0 0 16px #009983, 0 0 32px rgba(0,153,131,0.4)",
              animation: "mapLoadPulse 1.6s ease-in-out infinite",
            }}
          />
        </div>

        {/* Label */}
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 2.5,
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.5)",
              fontFamily: "'ATR Innova', sans-serif",
            }}
          >
            {label ? `Loading ${label} coverage` : "Updating map"}
          </div>
          {/* Animated dots */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 5,
              marginTop: 8,
            }}
          >
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                style={{
                  width: 4,
                  height: 4,
                  borderRadius: "50%",
                  background: "#009983",
                  animation: `mapLoadDot 1.2s ease-in-out ${i * 0.2}s infinite`,
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Keyframes injected once */}
      <style>{`
        @keyframes mapLoadOrbit {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes mapLoadPulse {
          0%, 100% { transform: translate(-50%, -50%) scale(1);   opacity: 1; }
          50%       { transform: translate(-50%, -50%) scale(1.5); opacity: 0.5; }
        }
        @keyframes mapLoadDot {
          0%, 80%, 100% { transform: scale(0.6); opacity: 0.3; }
          40%            { transform: scale(1.2); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
